import { useEffect, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'

interface TocItem {
  id: string
  text: string
  level: number
}

export default function TableOfContents() {
  const { group, slug } = useParams<{ group: string; slug?: string }>()
  const [items, setItems] = useState<TocItem[]>([])
  const [activeId, setActiveId] = useState('')
  const navRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const scan = () => {
      const main = document.querySelector('main')
      if (!main) return

      const headings = main.querySelectorAll('h2[id], h3[id]')
      const tocItems = Array.from(headings).map((h) => ({
        id: h.id,
        text: h.textContent?.replace('#', '').trim() || '',
        level: h.tagName === 'H3' ? 3 : 2,
      }))
      setItems(tocItems)
      setActiveId('')
    }

    // Delay to let content render
    const timeout = setTimeout(scan, 100)
    return () => clearTimeout(timeout)
  }, [group, slug])

  const isClickScrolling = useRef(false)

  useEffect(() => {
    if (items.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrolling.current) return
        
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id)
          }
        })
      },
      { rootMargin: '-12% 0px -75% 0px' }
    )

    items.forEach((item) => {
      const el = document.getElementById(item.id)
      if (el) observer.observe(el)
    })

    const handleScroll = () => {
      if (isClickScrolling.current) return
      
      // If we are at the very bottom of the page, forcefully select the last item
      if (
        window.innerHeight + Math.round(window.scrollY) >= 
        document.documentElement.scrollHeight - 50
      ) {
        setActiveId(items[items.length - 1].id)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', handleScroll)
    }
  }, [items])

  // Scroll active TOC item into view
  useEffect(() => {
    if (!activeId || !navRef.current) return
    const activeLink = navRef.current.querySelector(`[data-toc-id="${activeId}"]`) as HTMLElement | null
    if (!activeLink) return

    const navRect = navRef.current.getBoundingClientRect()
    const linkRect = activeLink.getBoundingClientRect()

    const isAbove = linkRect.top < navRect.top + 8
    const isBelow = linkRect.bottom > navRect.bottom - 8

    if (isAbove || isBelow) {
      activeLink.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [activeId])

  if (items.length === 0) return null

  return (
    <aside className="hidden w-[200px] shrink-0 xl:block">
      <div className="sticky top-32 px-2 py-6">
        <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-theme-muted">
          On this page
        </h2>
        <nav ref={navRef} className="relative max-h-[calc(100vh-20rem)] overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {items.map((item, index) => {
            const isL3 = item.level === 3
            const currX = isL3 ? 12 : 2
            const prevLevel = index > 0 ? items[index - 1].level : item.level
            const prevX = prevLevel === 3 ? 12 : 2
            const isActive = activeId === item.id

            return (
              <a
                key={item.id}
                href={`#${item.id}`}
                data-toc-id={item.id}
                onClick={(e) => {
                  e.preventDefault()
                  isClickScrolling.current = true
                  setActiveId(item.id)
                  document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  setTimeout(() => {
                    isClickScrolling.current = false
                  }, 1000)
                }}
                className={`group relative flex items-center py-1.5 transition-colors duration-300 ${
                  isActive
                    ? 'text-primary'
                    : 'text-theme-muted hover:text-theme-secondary'
                }`}
              >
                <svg
                  className="absolute left-0 top-0 h-full w-[24px] pointer-events-none transition-all duration-300 ease-in-out"
                  style={{ 
                    stroke: isActive ? 'currentColor' : 'var(--bg-border)', 
                    strokeWidth: 2, 
                    fill: 'none' 
                  }}
                >
                  {prevX !== currX ? (
                    <>
                      <line x1={prevX} y1="0" x2={currX} y2="12" strokeLinecap="square" />
                      <line x1={currX} y1="11" x2={currX} y2="100%" strokeLinecap="square" />
                    </>
                  ) : (
                    <line x1={currX} y1="0" x2={currX} y2="100%" strokeLinecap="square" />
                  )}
                </svg>
                <span className={`block break-words text-[13px] leading-snug ${isL3 ? 'pl-7' : 'pl-4'}`}>
                  {item.text}
                </span>
              </a>
            )
          })}
        </nav>
      </div>
    </aside>
  )
}
